/* eslint-disable @typescript-eslint/no-unused-vars */
import { useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { negate, uniqBy } from 'lodash/fp';
import { Dispatch, useCallback } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  ArticleFormat,
  ArticleStatus,
  GetArticleQuery,
  GetArticleQuery_query_article_Article_events_ArticleEvent,
  GetArticleQuery_query_article_Article_tags_ArticleTag,
  GetArticleQueryVariables,
  TagType,
} from '../../__generated__/queries-web';
import { useArticleUpsert } from '../../apollo/mutations/article.web.graphql';
import { GET_ARTICLE_QUERY } from '../../apollo/queries/articles.web.graphql';
import { paths } from '../../routing/Router/paths';
import { DATADOG_ACTIONS } from '../../tracking/actions';
import { actions, ArticleEditAction, ArticleState } from './reducer';
import { articleMutationVariables } from './utils/articleMutationVariables';
import { validateArticle } from './utils/validateArticle';

export const useSaveArticle = (
  article: ArticleState,
  dispatch: Dispatch<ArticleEditAction>,
) => {
  const client = useApolloClient();
  const { pushNotification } = useNotification();
  const history = useHistory();
  const [upsert, { loading }] = useArticleUpsert();
  const { id } = useParams();
  // const { data: getArticle } = useGetArticle(id);
  // const isDuplicatedArticle = window.location.href.search('/dup/');

  // TODO : Refacto the event tag behavior
  const handleEventsInTag = useCallback(
    (saveArticle: ArticleState) => {
      const isEvent = (
        save: GetArticleQuery_query_article_Article_tags_ArticleTag,
      ) => save.tag.type === ('Event' as TagType);
      const isTagEvent = negate(isEvent);
      const tags = saveArticle.tags.filter(isTagEvent);
      const eventsFromTag = saveArticle.tags.filter(isEvent);

      const newArticleEvents: GetArticleQuery_query_article_Article_events_ArticleEvent[] = eventsFromTag.map(
        (item, index) => ({
          order: index,
          __typename: 'ArticleEvent',
          category: {
            id: item.tag.id,
            title: item.tag.title,
            __typename: 'Category',
          },
        }),
      );
      // Helper function to normalize guide data to ArticleGuide format
      const normalizeGuide = (guide: any, index: number) => {
        const isArticleGuide = 'category' in guide && guide.category;
        const category = isArticleGuide ? guide.category : guide;
        const order = isArticleGuide ? guide.order : index;

        return {
          order,
          category: {
            id: category.id,
            title: category.title,
            __typename: 'Category' as const,
          },
          __typename: 'ArticleGuide' as const,
        };
      };

      const newArticleGuides = saveArticle.guides.map(normalizeGuide);
      const articleEvents = uniqBy((event) => event.category.id, [
        ...article.events,
        ...newArticleEvents,
      ]);

      return {
        ...saveArticle,
        tags,
        articleEvents,
        guides: newArticleGuides,
      };
    },
    [article],
  );

  const saveArticle = useCallback(
    async (forceArticle?: ArticleState, editedAt?: string | null) => {
      const finalArticle = handleEventsInTag(forceArticle || article);
      let prevArticleVersion;
      try {
        const prevArticleCachedVersion = client.readQuery<
          GetArticleQuery,
          GetArticleQueryVariables
        >({
          query: GET_ARTICLE_QUERY,
          variables: {
            where: {
              id: finalArticle.id,
            },
          },
        });
        prevArticleVersion = prevArticleCachedVersion
          ? prevArticleCachedVersion.article
          : null;
      } catch {
        prevArticleVersion = null;
      }

      const { isValid, errorMessage } = validateArticle(
        finalArticle,
        prevArticleVersion,
      );
      if (!isValid) {
        /**
         * if the article has errors when switching between status let's get back
         * to the initial article's status
         */
        if (finalArticle.status !== prevArticleVersion?.status) {
          dispatch(
            actions.updatePublishDateAndStatus({
              publishDate: prevArticleVersion?.publishedAt || undefined,
              status: prevArticleVersion?.status || ArticleStatus.Draft,
            }),
          );
        }
        pushNotification({
          type: NotificationTypeEnum.error,
          message: errorMessage,
        });

        datadogRum.addError(errorMessage, {
          action: DATADOG_ACTIONS.SAVE_ARTICLE,
        });

        return;
      }
      try {
        datadogRum.addAction(DATADOG_ACTIONS.SAVE_ARTICLE, {
          article: finalArticle,
        });

        const result = await upsert({
          variables: articleMutationVariables(
            finalArticle,
            prevArticleVersion,
            editedAt,
          ),
        });

        if (!result || !result.data || !result.data.upsertArticle) {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: "Erreur lors de l'enregistrement",
          });
          return;
        }

        /**
         * if we have no previous article version it's a newly created one
         * so redirect to the url with its new id
         */
        if (!prevArticleVersion) {
          history.replace(
            generatePath(paths.ARTICLE_EDIT, {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              brandKey: finalArticle.brandKey!,
              articleType:
                finalArticle.format === ArticleFormat.Rich
                  ? 'article'
                  : 'video',
              id: result.data.upsertArticle.id,
            }),
          );
        }

        pushNotification({
          type: NotificationTypeEnum.success,
          message: 'Article enregistré',
        });
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          const catchMessage = (error as { message: string }).message as string;
          let message = "Erreur lors de l'enregistrement";

          if (catchMessage.includes('HTML')) {
            const matched = catchMessage.match(/<[^>/]\w*/);
            message = `L'insertion de code ${
              matched ? matched[0] : '</'
            }> n'est pas autorisé dans un article.`;
          }
          pushNotification({
            type: NotificationTypeEnum.error,
            message,
          });
        } else {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: "Erreur lors de l'enregistrement",
          });
        }
      }
    },
    [
      article,
      pushNotification,
      client,
      upsert,
      history,
      dispatch,
      handleEventsInTag,
    ],
  );

  return {
    saveArticle,
    loading,
  };
};
/* eslint-enable @typescript-eslint/no-unused-vars */
